import { JSX, useContext, useEffect, useRef, useState } from "react";
import NfsButton from "../genericComponents/NfsButton/NfsButton";
import { toast } from "react-toastify";
import { generateFeaturesFromFiles, getMapView, goToLayerInMap } from "../../utils/arcgisUtils";

import TrashIcon from "../../assets/trash-can.svg?react";
import PinIcon from "../../assets/location-pin.svg?react";
import "./DataImport.css";
import { AppConfig } from "../../AppConfig";
import { getPortalItemsFromGroup } from "../../utils/portalUtils";
import { UserContext } from "../../Context";
import PortalItem from "@arcgis/core/portal/PortalItem";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import MapImageLayer from "@arcgis/core/layers/MapImageLayer";
import { storeMapSettingsInLocalStorage } from "../../utils/layerlistUtils";
import { getMapSettings } from "../../utils/localStorageUtils";
import type Layer from "@arcgis/core/layers/Layer";

type SelectedImportFile = { file: File; srid?: number };

const DataImport = () => {
    const userContext = useContext(UserContext);
    const [selectedFiles, setSelectedFiles] = useState<SelectedImportFile[]>([]);
    const [customLayers, setCustomLayers] = useState<Layer[]>([]);
    const [allCadLayers, setAllCadLayers] = useState<PortalItem[]>([]);
    const [usersCadLayers, setUsersCadLayers] = useState<PortalItem[]>([]);
    const [cadLayerVisibility, setCadLayerVisibility] = useState<Record<string, boolean>>({});
    const validFileExtensions = [
        ".xml",
        ".cpg",
        ".prj",
        ".dbf",
        ".sbn",
        ".sbx",
        ".shx",
        ".shp",
        ".json",
        ".geojson",
        ".gpx",
        ".dwg",
        ".dxf",
    ];

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    // Drag and drop area state and handlers
    const dropZoneRef = useRef<HTMLDivElement | null>(null);
    const [isDragActive, setIsDragActive] = useState(false);

    const isCadFile = (fileName: string) =>
        fileName.toLowerCase().endsWith(".dwg") || fileName.toLowerCase().endsWith(".dxf");

    const normalizeCadName = (fileName: string) => {
        let normalizedName = fileName
            .replaceAll("æ", "ae")
            .replaceAll("ø", "oe")
            .replaceAll("å", "aa")
            .replaceAll("Æ", "Ae")
            .replaceAll("Ø", "Oe")
            .replaceAll("Å", "Aa");

        normalizedName = normalizedName.replaceAll(" ", "_");
        normalizedName = normalizedName.replaceAll("-", "_");
        normalizedName = normalizedName.replaceAll(/[^a-zA-Z0-9.\\-]/g, "_");
        normalizedName = normalizedName.replace(/\.[^/.]+$/, "");

        return normalizedName.toLowerCase();
    };

    const toSelectedImportFile = (file: File): SelectedImportFile => ({
        file,
        srid: isCadFile(file.name) ? 32633 : undefined,
    });

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragActive(true);
    };

    const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragActive(false);
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragActive(false);
        const files = event.dataTransfer.files;
        if (files && files.length > 0) {
            // Filter out unsupported file types
            const invalidFileNames = Array.from(files)
                .filter((file) => !validFileExtensions.some((ext) => file.name.toLowerCase().endsWith(ext)))
                .map((file) => file.name);

            const filteredFiles = Array.from(files).filter((file) =>
                validFileExtensions.some((ext) => file.name.toLowerCase().endsWith(ext))
            );
            if (invalidFileNames.length > 0) {
                toast.error(
                    `Følgende filer har et format som ikke støttes og ble ikke lagt til: ${invalidFileNames.join(", ")}`
                );
            }

            // Only add new files to the state
            const filesToAdd = filteredFiles
                .filter((newFile) => !selectedFiles.some((existingFile) => existingFile.file.name === newFile.name))
                .map((file) => toSelectedImportFile(file));

            setSelectedFiles((prev) => [...prev, ...filesToAdd]);
        }
    };

    const getCadLayerTitle = (item: PortalItem) => "import_" + item.title;
    const getCadLayerKey = (item: PortalItem): string => String(item.id ?? item.title ?? "");

    const getCadLayerVisibleInMap = (item: PortalItem) => {
        const mapview = getMapView();
        if (!mapview?.map) return false;
        const existingLayer = mapview.map.allLayers.find((layer) => layer.title === getCadLayerTitle(item));
        return !!existingLayer?.visible;
    };

    const refreshCadLayerVisibility = (items: PortalItem[]) => {
        const visibilityMap: Record<string, boolean> = {};
        items.forEach((item) => {
            visibilityMap[getCadLayerKey(item)] = getCadLayerVisibleInMap(item);
        });
        setCadLayerVisibility((prev) => ({ ...prev, ...visibilityMap }));
    };

    // Update custom layers whenever needed
    const updateCustomLayers = async () => {
        const mapview = getMapView();
        const user = userContext?.user;
        const userSelf = userContext?.userSelf;
        const userId = user?.value?.id ?? userSelf?.value?.id;

        const allItems = await getPortalItemsFromGroup(AppConfig.CadLayerGroupId);
        setAllCadLayers(allItems);

        let userItems: PortalItem[] = [];
        if (userId) {
            userItems = await getPortalItemsFromGroup(AppConfig.CadLayerGroupId, userId);
            setUsersCadLayers(userItems);
        } else {
            setUsersCadLayers([]);
        }

        refreshCadLayerVisibility([...allItems, ...userItems]);

        if (!mapview?.map) {
            return { allItems, userItems };
        }

        const layers = mapview.map.allLayers.filter((layer) => layer.title?.startsWith("Import_") ?? false).toArray();

        setCustomLayers(layers);

        return { allItems, userItems };
    };

    // Call updateCustomLayers when component mounts and when needed
    useEffect(() => {
        void updateCustomLayers();
        return () => {
            setSelectedFiles([]);
        };
    }, []);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files) {
            // Filter out unsupported file types
            const filteredFiles = Array.from(files).filter((file) =>
                validFileExtensions.some((ext) => file.name.toLowerCase().endsWith(ext))
            );
            const invalidFileNames = Array.from(files)
                .filter((file) => !validFileExtensions.some((ext) => file.name.toLowerCase().endsWith(ext)))
                .map((file) => file.name);
            if (invalidFileNames.length > 0) {
                toast.error(
                    `Følgende filer har et format som ikke støttes og ble ikke lagt til: ${invalidFileNames.join(", ")}`
                );
            }
            // Get new files that aren't already in the selected files list
            const newFiles = filteredFiles.filter(
                (newFile) => !selectedFiles.some((existingFile) => existingFile.file.name === newFile.name)
            );

            // Append the new files to the existing ones
            setSelectedFiles((prev) => [...prev, ...newFiles.map((file) => toSelectedImportFile(file))]);
        }

        event.target.value = "";
    };

    const importFiles = async () => {
        if (selectedFiles.length === 0) {
            toast.error("Ingen filer valgt");
            return;
        }

        const processedCadNames = new Set(
            selectedFiles
                .filter((selectedFile) => isCadFile(selectedFile.file.name))
                .map((selectedFile) => normalizeCadName(selectedFile.file.name))
        );

        await generateFeaturesFromFiles(selectedFiles);

        const updatedItems = await updateCustomLayers();

        if (processedCadNames.size > 0 && updatedItems) {
            const allUpdatedCadItems = [...updatedItems.allItems, ...updatedItems.userItems];
            const uniqueCadItems = Array.from(new Map(allUpdatedCadItems.map((item) => [item.id, item])).values());

            uniqueCadItems
                .filter((item) => processedCadNames.has(normalizeCadName(item.title ?? "")))
                .forEach((item) => addCadLayerToMap(item));
        }

        setSelectedFiles([]);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
        void updateCustomLayers(); // Update layers after adding new ones
    };

    const removeLayer = (layerId: string) => {
        const mapview = getMapView();
        if (!mapview?.map) return;

        const layer = mapview.map.allLayers.find((layer) => layer.id === layerId);
        if (layer) {
            mapview.map.remove(layer);
            void updateCustomLayers(); // Update layers after removal
        }
    };

    const renderCustomDataLayers = () => {
        if (customLayers.length === 0) {
            return null;
        }
        return (
            <div className="flex-col small-gap">
                <span>
                    <strong>Importerte data:</strong>
                </span>
                {customLayers.map((layer) => (
                    <div className="flex-row space-between w-100" key={layer.id}>
                        <span className="break-all" style={{ maxWidth: "100%" }}>
                            {layer.title}
                        </span>
                        <button
                            className="nfs-button fit-content no-flex outline warning"
                            onClick={() => removeLayer(layer.id)}
                        >
                            <TrashIcon />
                        </button>
                    </div>
                ))}
            </div>
        );
    };

    const renderCadLayers = (cadLayers: PortalItem[]) => {
        if (cadLayers.length === 0) {
            return null;
        }
        const mapview = getMapView();
        if (!mapview?.map) return null;
        const listOfLayerButtons: JSX.Element[] = [];
        cadLayers.forEach((item) => {
            const isLayerVisible = cadLayerVisibility[getCadLayerKey(item)] ?? getCadLayerVisibleInMap(item);
            if (isLayerVisible) {
                listOfLayerButtons.push(
                    <div className="flex-row space-between small-gap w-100" key={item.id}>
                        <span className="break-all" style={{ maxWidth: "100%" }}>
                            {item.title}
                        </span>
                        <NfsButton
                            className="no-flex fit-content ml-auto"
                            outlined
                            round
                            onClick={() => goToLayerInMap(item, addCadLayerToMap)}
                        >
                            <PinIcon />
                        </NfsButton>
                        <NfsButton
                            className="warning no-flex cad-layer-toggle-button"
                            outlined
                            onClick={() => removeCadLayerFromMap(item)}
                        >
                            Skjul
                        </NfsButton>
                    </div>
                );
            } else {
                listOfLayerButtons.push(
                    <div className="flex-row space-between small-gap w-100" key={item.id}>
                        <span className="break-all" style={{ maxWidth: "100%" }}>
                            {item.title}
                        </span>
                        <NfsButton
                            className="no-flex fit-content ml-auto"
                            outlined
                            round
                            onClick={() => goToLayerInMap(item, addCadLayerToMap)}
                        >
                            <PinIcon />
                        </NfsButton>
                        <NfsButton className="no-flex cad-layer-toggle-button" onClick={() => addCadLayerToMap(item)}>
                            Vis
                        </NfsButton>
                    </div>
                );
            }
        });
        return <div className="flex-col small-gap">{listOfLayerButtons}</div>;
    };

    const addCadLayerToMap = (item: PortalItem) => {
        const mapview = getMapView();
        if (!mapview?.map) return;
        const existingLayer = mapview?.map?.allLayers.find((layer) => layer.title === getCadLayerTitle(item));
        if (existingLayer) {
            existingLayer.visible = true;
            setCadLayerVisibility((prev) => ({ ...prev, [getCadLayerKey(item)]: true }));
            return;
        }
        const newLayer = new MapImageLayer({
            portalItem: {
                id: item.id,
                portal: {
                    url: AppConfig.ArcGIS.BaseUrl,
                },
            },
            url: item.url,
            title: getCadLayerTitle(item),
        });
        mapview.map.add(newLayer);
        setCadLayerVisibility((prev) => ({ ...prev, [getCadLayerKey(item)]: true }));
        const mapSettings = getMapSettings();
        const labels = mapSettings?.enabledLabels ?? [];
        storeMapSettingsInLocalStorage(labels, mapSettings?.labelsEnabled ?? false);
    };

    const removeCadLayerFromMap = (item: PortalItem) => {
        const mapview = getMapView();
        if (!mapview?.map) return;
        const existingLayer = mapview?.map?.allLayers.find((layer) => layer.title === getCadLayerTitle(item));
        if (existingLayer) {
            existingLayer.visible = false;
            setCadLayerVisibility((prev) => ({ ...prev, [getCadLayerKey(item)]: false }));
        }
        const mapSettings = getMapSettings();
        const labels = mapSettings?.enabledLabels ?? [];
        storeMapSettingsInLocalStorage(labels, mapSettings?.labelsEnabled ?? false);
    };

    const removeFile = (fileName: string) => {
        setSelectedFiles((prevFiles) => prevFiles.filter((fileEntry) => fileEntry.file.name !== fileName));
    };

    const updateFileSrid = (fileName: string, srid: number) => {
        setSelectedFiles((prevFiles) =>
            prevFiles.map((fileEntry) => {
                if (fileEntry.file.name !== fileName) return fileEntry;
                return { ...fileEntry, srid };
            })
        );
    };

    return (
        <div className="efs-manager-content-container small-gap">
            <span>Støttede formater: Shapefile, GeoJSON, GPX, DWG og DXF</span>
            <span>
                OBS: For shapefiles må minimum følgende filer (.shp, .shx, .dbf, og .prj) være inkludert for at importen
                skal fungere.
            </span>
            <span>OBS: Filer med samme filnavn blir gruppert og prosessert sammen som en filpakke.</span>
            <span>Prosesseringen kan ta litt tid, avhengig av filstørrelse og kompleksitet.</span>
            <span>Jo flere filer du prosesser samtidig, jo lengre tid kan det ta.</span>
            <label
                style={{ cursor: "pointer" }}
                role="button"
                className="nfs-button no-flex outline"
                htmlFor="file-import"
            >
                Velg filer
                <input
                    hidden
                    id="file-import"
                    name="file-import"
                    ref={fileInputRef}
                    placeholder="Velg filer"
                    type="file"
                    multiple
                    onClick={(event) => {
                        event.currentTarget.value = "";
                    }}
                    onChange={handleFileChange}
                />
            </label>

            {/* Drag and drop area */}
            <div
                ref={dropZoneRef}
                className={`dataimport-dropzone${isDragActive ? " active" : ""}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                {isDragActive ? "Slipp filene her..." : "Eller dra og slipp filer her"}
            </div>

            <NfsButton disabled={selectedFiles.length === 0} className="no-flex" onClick={importFiles}>
                Importer og legg til i kartet
            </NfsButton>
            <span>
                <strong>Valgte filer:</strong>
            </span>
            {selectedFiles.map((selectedFile) => (
                <div className="flex-row space-between small-gap w-100" key={selectedFile.file.name}>
                    <div className="flex-col small-gap w-100">
                        <span className="break-all">{selectedFile.file.name}</span>
                        {isCadFile(selectedFile.file.name) && (
                            <div className="flex-row small-gap align-center">
                                <span>SRID:</span>
                                <input
                                    type="number"
                                    min={1}
                                    value={selectedFile.srid ?? 32633}
                                    onChange={(event) =>
                                        updateFileSrid(selectedFile.file.name, Number(event.target.value || 32633))
                                    }
                                />
                            </div>
                        )}
                    </div>
                    <button
                        className="nfs-button no-flex outline warning"
                        onClick={() => removeFile(selectedFile.file.name)}
                    >
                        <TrashIcon />
                    </button>
                </div>
            ))}
            <div className="efs-manager-content">
                {renderCustomDataLayers()}
                {allCadLayers.length > 0 && (
                    <div>
                        <span>
                            <strong>Tilgjengelige CAD-lag:</strong>
                        </span>
                        <Tabs
                            className={`w-100 h-100 flex-col`}
                            disabledTabClassName="disabled"
                            selectedTabClassName="selected"
                        >
                            <TabList className={`panel-content-tab-list`}>
                                <Tab className={`panel-content-tab-button`} title="Kartdata">
                                    Mine CAD-lag
                                </Tab>
                                <Tab className={`panel-content-tab-button`} title="Dybdedata">
                                    Alle CAD-lag
                                </Tab>
                            </TabList>
                            <TabPanel selectedClassName="selected" className={`panel-content-tab-panel`}>
                                {renderCadLayers(usersCadLayers)}
                            </TabPanel>
                            <TabPanel selectedClassName="selected" className={`panel-content-tab-panel`}>
                                {renderCadLayers(allCadLayers)}
                            </TabPanel>
                        </Tabs>
                    </div>
                )}
                <div className="efs-manager-footer"></div>
            </div>
        </div>
    );
};

export default DataImport;
